import React   from "react";
import values  from "../../../../../../values";
import Context from "../../../../../../context";

import {
  ICityItem,
  ICompanyItem,
  IItem,
  IOnSearchUser,
  IProvinceItem,
} from "../../../../../../types/common.interface";
import { useFormik } from "formik";
import { ICreateAndUpdateDriver } from "../../../../../../types/driver.interface";
import { ICreateAndUpdateCompany } from "../../../../../../types/company.interface";
import {
  CustomButton2,
  CustomDropdownMultiSearch,
  CustomDropdownSearch,
  CustomInput,
  CustomView, LocationDropdowns,
} from "../../../../../../components";

import * as Yup from "yup";
import TitleLine from "../../../../../../components/TitleLine";
import validations from "../../../../../../validations";
import { useTranslation } from "react-i18next";
import { gender, marital } from "../../../../../../values/dropdownItems";
import {useQuery} from "react-query";
import api from "../../../../../../api";
import {locationNamespace} from "../../../../../../types/location.namespace";

type Props = {
  defaultValues: ICreateAndUpdateDriver;
  onSubmit     : (event: ICreateAndUpdateDriver) => void;
};

const AdministratorDriverInformation: React.FC<Props> = (props: Props): React.ReactElement => {

  // # =================================================================================================================
  // # --------------------------------------------- Start Validate Values ---------------------------------------------

  const { t } = useTranslation();
  const { isLoading, setShowModal } = React.useContext(Context.DriverScreen);

  const [selectedGender, setSelectedGender] = React.useState<IItem | undefined>(props?.defaultValues?.gender);
  const [selectedMarital, setSelectedMarital] = React.useState<IItem | undefined>(props?.defaultValues?.marital);
  const [selectedCompany, setSelectedCompany] = React.useState<ICreateAndUpdateCompany | undefined>(props?.defaultValues?.company);
  const [searchedValue, setSearchedValue] = React.useState<IOnSearchUser>();

  const [selectedCity        , setSelectedCity        ] = React.useState<locationNamespace.city        .index | undefined>(undefined);
  const [selectedArea        , setSelectedArea        ] = React.useState<locationNamespace.area        .index | undefined>(undefined);
  const [selectedProvince    , setSelectedProvince    ] = React.useState<locationNamespace.province    .index | undefined>(undefined);
  const [selectedNeighborhood, setSelectedNeighborhood] = React.useState<locationNamespace.neighborhood.index | undefined>(undefined);

  const [dropDownErrors, setDropDownErrors] = React.useState<any>({
    province: '',
    marital : '',
    company : '',
    gender  : '',
    city    : '',
  });

  // # ---------------------------------------------- End Validate Values ----------------------------------------------
  // # =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------------ Start Call API -------------------------------------------------

  const getAllCompany = useQuery(
    [values.useQueryKeys.getAllCompany, searchedValue],
    () => api.post.administrator.getAllCompany({
      search: {
        base: searchedValue?.base.value,
        value: searchedValue?.searchedValue,
      },
    })
  );

  // ! ------------------------------------------------- End Call API --------------------------------------------------
  // ! =================================================================================================================

  // * =================================================================================================================
  // * -------------------------------------------- Start Local Functions ----------------------------------------------

  const formik = useFormik<ICreateAndUpdateDriver>({
    enableReinitialize: true,
    initialValues: {
      address     : props.defaultValues.address      ? props.defaultValues.address      : '',
      lastName    : props.defaultValues.lastName     ? props.defaultValues.lastName     : '',
      firstName   : props.defaultValues.firstName    ? props.defaultValues.firstName    : '',
      kartNumber  : props.defaultValues.kartNumber   ? props.defaultValues.kartNumber   : '',
      phoneNumber : props.defaultValues.phoneNumber  ? props.defaultValues.phoneNumber  : '',
      shabaNumber : props.defaultValues.shabaNumber  ? props.defaultValues.shabaNumber  : '',
      nationalCode: props.defaultValues.nationalCode ? props.defaultValues.nationalCode : '',
    },
    validationSchema: Yup.object({
      address     : validations.yupValidations.address     ,
      lastName    : validations.yupValidations.lastName    ,
      firstName   : validations.yupValidations.firstName   ,
      kartNumber  : validations.yupValidations.kartNumber  ,
      phoneNumber : validations.yupValidations.phoneNumber ,
      shabaNumber : validations.yupValidations.shabaNumber ,
      nationalCode: validations.yupValidations.nationalCode,
    }),
    onSubmit: (values: ICreateAndUpdateDriver, { setSubmitting }) => {
      const formValues: ICreateAndUpdateDriver = {
        ...values,
        gender : selectedGender ,
        company: selectedCompany,
        marital: selectedMarital,

        city        : selectedCity        ?._id,
        area        : selectedArea        ?._id,
        province    : selectedProvince    ?._id,
        neighborhood: selectedNeighborhood?._id,
      };
      if (
        selectedProvince &&
        selectedCity &&
        selectedCompany &&
        selectedMarital &&
        selectedGender
      ) {
        Promise.resolve(props.onSubmit(formValues)).catch((e) =>
          setSubmitting(false)
        );
      }
    },
  });

  function submitHandler(): void {
    let dropDownErrorsList = {city: '', gender: '', marital: '', company: '', province: ''};

    if (!selectedGender ) dropDownErrorsList = {...dropDownErrorsList, gender : t('gender_is_required' )}
    if (!selectedMarital) dropDownErrorsList = {...dropDownErrorsList, marital: t('marital_is_required')}
    if (!selectedCompany) dropDownErrorsList = {...dropDownErrorsList, company: t('company_is_required')}

    if (!selectedProvince) {
      dropDownErrorsList = {...dropDownErrorsList, province: t("province_is_required"), city: t("city_is_required")};
    } else if (!selectedCity) {
      dropDownErrorsList = {...dropDownErrorsList, city: t("city_is_required")};
    }

    setDropDownErrors(dropDownErrorsList);

    formik.submitForm();
  }

  function onSelectProvince(province: locationNamespace.province.index): void {
    setSelectedNeighborhood(undefined);
    setSelectedProvince    (province );
    setSelectedCity        (undefined);
    setSelectedArea        (undefined);
    setDropDownErrors({
      ...dropDownErrors,
      province: '',
      city    : '',
    });
  }

  function onSelectCity(city: locationNamespace.city.index): void {
    setSelectedNeighborhood(undefined);
    setSelectedCity        (city     );
    setSelectedArea        (undefined);
    setDropDownErrors({
      ...dropDownErrors,
      city: '',
    });
  }

  function onSelectArea(area: locationNamespace.area.index): void {
    setSelectedNeighborhood(undefined);
    setSelectedArea        (area);
  }

  function onSelectNeighborhood(neighborhood: locationNamespace.neighborhood.index): void {
    setSelectedNeighborhood(neighborhood);
  }

  // * --------------------------------------------- End Local Functions -----------------------------------------------
  // * =================================================================================================================

  // ! =================================================================================================================
  // ! ------------------------------------------ Start UseEffect functions --------------------------------------------

  const companyInformationWithCompanyName = getAllCompany?.data?.result?.map(
    (item: any) => ({...item, companyName: item?.companyInformation?.companyName})
  );

  // ! ------------------------------------------- End UseEffect functions ---------------------------------------------
  // ! =================================================================================================================

  // ? =================================================================================================================
  // ? ----------------------------------------------- Start Return View -----------------------------------------------

  return (
    <form onSubmit={submitHandler}>
      <CustomView.Container className="create-form-content">
        <TitleLine title={t("driverInfo")} />
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("company")}</label>
            <CustomDropdownMultiSearch
              isSearch
              baseSearch={values.dropdownBaseSearch.COMPANY_BASE_SEARCH.map(
                (item) => ({...item, title: t(item.title)})
              )}
              items={companyInformationWithCompanyName}
              itemName="companyName"
              onSelectItem={(event: ICompanyItem) => {
                setSelectedCompany(event);
                setDropDownErrors({ ...dropDownErrors, company: "" });
              }}
              defaultItem={selectedCompany}
              searchedValue={searchedValue}
              setSearchedValue={setSearchedValue}
              placeHolder={t("selectCompany")}
              isLoading={getAllCompany.isLoading}
              selectedBaseSearch={ searchedValue?.base?.value === "nationalCode" ?
                "companyInformation.nationalCode" : searchedValue?.base?.value
              }
            />
            <div className="error">
              {dropDownErrors.company !== "" && dropDownErrors.company}
            </div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <CustomInput
              name="phoneNumber"
              id="phoneNumber"
              label={t("phoneNumber")}
              value={formik.values.phoneNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.phoneNumber && formik.touched.phoneNumber}
              errorText={formik.errors.phoneNumber}
              maxLength={11}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="firstName"
              id="firstName"
              label={t("firstName")}
              value={formik.values.firstName}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.firstName && formik.touched.firstName}
              errorText={formik.errors.firstName}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="lastName"
              id="lastName"
              label={t("lastName")}
              value={formik.values.lastName}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.lastName && formik.touched.lastName}
              errorText={formik.errors.lastName}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <LocationDropdowns.Province
              defaultItem  = { selectedProvince }
              onSelectItem = { onSelectProvince }
              isError   = { dropDownErrors.province }
              errorText = { dropDownErrors.province }
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <LocationDropdowns.City
              defaultItem      = { selectedCity     }
              onSelectItem     = { onSelectCity     }
              selectedProvince = { selectedProvince }
              isError   = { dropDownErrors.city }
              errorText = { dropDownErrors.city }
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <LocationDropdowns.Area
              defaultItem      = { selectedArea     }
              onSelectItem     = { onSelectArea     }
              selectedCity     = { selectedCity     }
              selectedProvince = { selectedProvince }
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <LocationDropdowns.Neighborhood
              defaultItem      = { selectedNeighborhood }
              onSelectItem     = { onSelectNeighborhood }
              selectedArea     = { selectedArea         }
              selectedCity     = { selectedCity         }
              selectedProvince = { selectedProvince     }
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("gender")}</label>
            <CustomDropdownSearch
              items={gender.map((item) => {return { ...item, name: t(item.name as string) };})}
              itemName="name"
              placeHolder={t("selectGender")}
              defaultItem={selectedGender}
              onSelectItem={(event: any) => {
                setSelectedGender(event);
                setDropDownErrors({ ...dropDownErrors, gender: "" });
              }}
            />
            <div className="error">{dropDownErrors.gender !== "" && dropDownErrors.gender}</div>
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum dropdown">
            <label className="form-label">{t("marital")}</label>
            <CustomDropdownSearch
              items={marital.map((item) => {return { ...item, name: t(item.name as string) };})}
              itemName="name"
              placeHolder={t("selectMarital")}
              defaultItem={selectedMarital}
              onSelectItem={(event: any) => {
                setSelectedMarital(event);
                setDropDownErrors({ ...dropDownErrors, marital: "" });
              }}
            />
            <div className="error">{dropDownErrors.marital !== "" && dropDownErrors.marital}</div>
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="nationalCode"
              id="nationalCode"
              label={t("nationalCode")}
              value={formik.values.nationalCode}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.nationalCode && formik.touched.nationalCode}
              errorText={formik.errors.nationalCode}
              maxLength={10}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="kartNumber"
              id="kartNumber"
              label={t("cardNumber")}
              value={formik.values.kartNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.kartNumber && formik.touched.kartNumber}
              errorText={formik.errors.kartNumber}
              maxLength={16}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="address"
              id="address"
              label={t("address")}
              value={formik.values.address}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.address && formik.touched.address}
              errorText={formik.errors.address}
            />
          </CustomView.Column>
          <CustomView.Column medium={6} className="form-colum">
            <CustomInput
              name="shabaNumber"
              id="shabaNumber"
              label={t("shabaNumber")}
              value={formik.values.shabaNumber}
              onValueChange={formik.handleChange}
              formikProps={{ onBlur: formik.handleBlur }}
              error={formik.errors.shabaNumber && formik.touched.shabaNumber}
              errorText={formik.errors.shabaNumber}
              isShaba
              maxLength={24}
            />
          </CustomView.Column>
        </CustomView.Row>
        <CustomView.Row className="form-row">
          <CustomView.Column large={3} medium={4} small={6} className="form-colum">
            <CustomButton2
              isLoading={isLoading}
              onClick={() => setShowModal!("NONE")}
              title={t("cancel")}
              type="danger"
            />
          </CustomView.Column>
          <CustomView.Column large={3} medium={4} small={6} className="form-colum">
            <CustomButton2 isLoading={isLoading} onClick={submitHandler} title={t("next")}/>
          </CustomView.Column>
        </CustomView.Row>
      </CustomView.Container>
    </form>
  );

  // ? ------------------------------------------------ End Return View ------------------------------------------------
  // ? =================================================================================================================

};

export default AdministratorDriverInformation;
